import React from "react";
import { ChevronDown } from "lucide-react";
import BaseSectionCard from "@/components/common/BaseSectionCard";

const SectionCard = ({
  title,
  subtitle,
  step,
  optionalTag,
  headerAction,
  children,
  allowOverflow,
  className = "",
  contentClassName = "",
  collapsible = false,
  isExpanded = true,
  onToggle,
}) => {
  const hasBody = children != null && children !== false;
  const isChip = collapsible && !isExpanded;

  const handleToggle = () => {
    if (collapsible && onToggle) onToggle();
  };

  const handleToggleKeyDown = (e) => {
    if (!collapsible || !onToggle) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  const stopContentPropagation = (e) => {
    if (collapsible && isExpanded) e.stopPropagation();
  };

  const stepBadge =
    step != null ? (
      <span className="inline-flex shrink-0 rounded-md bg-cyan-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cyan-300 border border-cyan-400/25">
        Step {step}
      </span>
    ) : optionalTag ? (
      <span className="inline-flex shrink-0 rounded-md bg-gray-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 border border-gray-500/30">
        {optionalTag}
      </span>
    ) : null;

  const titleEl = (
    <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-cyan-300/95 leading-tight">
      {title}
    </span>
  );

  const chevronEl = collapsible && onToggle && (
    <ChevronDown
      aria-hidden
      className={`h-3.5 w-3.5 shrink-0 text-cyan-400/70 pointer-events-none transition-transform duration-200 ${
        isExpanded ? "rotate-180" : ""
      }`}
    />
  );

  const headerBlock = isChip ? (
    <div className="inline-flex items-center gap-2 min-w-0 max-w-full">
      {stepBadge}
      {titleEl}
      {chevronEl}
    </div>
  ) : (
    <div
      className={`flex items-start gap-3 min-w-0 w-full ${
        collapsible && isExpanded
          ? "cursor-pointer rounded-xl -mx-1 px-1 py-1 hover:bg-white/[0.03] transition-colors"
          : ""
      } ${headerAction ? "justify-between" : ""}`}
      role={collapsible && isExpanded ? "button" : undefined}
      tabIndex={collapsible && isExpanded ? 0 : undefined}
      aria-expanded={collapsible && isExpanded ? isExpanded : undefined}
      onClick={collapsible && isExpanded ? handleToggle : undefined}
      onKeyDown={collapsible && isExpanded ? handleToggleKeyDown : undefined}
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {stepBadge}
        <p className="text-sm sm:text-base font-semibold uppercase tracking-wide text-cyan-300 break-words min-w-0">
          {title}
        </p>
        {chevronEl}
      </div>
      {headerAction && (
        <div
          className="flex shrink-0 items-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {headerAction}
        </div>
      )}
    </div>
  );

  return (
    <BaseSectionCard
      as={collapsible ? "div" : "section"}
      role={isChip ? "button" : undefined}
      tabIndex={isChip ? 0 : undefined}
      aria-expanded={collapsible ? isExpanded : undefined}
      onClick={isChip ? handleToggle : undefined}
      onKeyDown={isChip ? handleToggleKeyDown : undefined}
      decorativeGlow={!collapsible || isExpanded}
      className={`min-w-0 ${
        allowOverflow ? "overflow-visible" : "overflow-hidden"
      } ${
        isChip
          ? "!p-0 w-auto max-w-full shrink-0 cursor-pointer rounded-xl border-cyan-500/20 bg-gradient-to-br from-gray-900/90 to-gray-950/90 hover:border-cyan-400/35 hover:shadow-md hover:shadow-cyan-500/5 active:scale-[0.99] transition-all duration-200"
          : collapsible && isExpanded
            ? "w-full basis-full !p-4 sm:!p-6 lg:!p-8"
            : ""
      } ${className}`}
      contentClassName={`min-w-0 ${
        isChip
          ? "px-3 py-2.5 sm:px-3.5 sm:py-2.5"
          : collapsible && isExpanded
            ? "space-y-4"
            : "space-y-4"
      } ${contentClassName}`}
    >
      {headerBlock}
      {subtitle && !isChip && (
        <p
          className={`text-sm text-gray-400 leading-relaxed ${
            collapsible && isExpanded ? "cursor-pointer" : ""
          }`}
          onClick={collapsible && isExpanded ? handleToggle : undefined}
        >
          {subtitle}
        </p>
      )}
      {hasBody && (
        <div className="min-w-0" onClick={stopContentPropagation}>
          {children}
        </div>
      )}
    </BaseSectionCard>
  );
};

export default SectionCard;
